import { IIncidentHistoryEvent, IIncidentHistoryUser } from '../../../../../../types/incidents.types';
import {
  ILogisticServiceHistory,
  ILogisticServiceHistoryEvent
} from '../../../../../../types/suscription/history.types';

/**
 * Construye el nombre completo del usuario o devuelve null si faltan datos.
 */
const buildUserFullName = (user: IIncidentHistoryUser | null): string | null => {
  if (!user) return null;
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName.length > 0 ? fullName : null;
};

/**
 * Construye el `eventType.name` (título visible en `subStepHeaderMain`) y la
 * `eventType.description` (descripción visible debajo) para cada tipo de
 * evento del ciclo de vida de un incidente:
 * - opened: se levantó el reporte
 * - escalated: el reporte se reabrió con otra razón sobre el mismo incidente
 * - cancelled: la razón del reporte canceló el servicio logístico
 * - closed: el reporte se cerró manualmente
 */
const buildIncidentEventTexts = (event: IIncidentHistoryEvent): { name: string; description: string } => {
  const fullName = buildUserFullName(event.user);

  switch (event.type) {
    case 'opened':
      return {
        name: fullName ? `${fullName} levantó un reporte` : 'Reporte levantado',
        description: event.reasonName
      };

    case 'escalated':
      return {
        name: fullName ? `${fullName} escaló el reporte` : 'Reporte escalado',
        description: event.reasonName
      };

    case 'cancelled':
      return {
        name: fullName ? `${fullName} canceló el servicio` : 'Servicio cancelado',
        description: `Se canceló mediante el reporte ${event.reasonName}`
      };

    case 'closed':
    default:
      return {
        name: 'Reporte cerrado',
        description: fullName
          ? `${fullName} cerró el reporte ${event.reasonName}`
          : `Se cerró el reporte ${event.reasonName}`
      };
  }
};

/**
 * Transforma los eventos planos de incidentes que vienen del backend al
 * formato `ILogisticServiceHistoryEvent` consumido por el stepper, para
 * poder mezclarlos con los eventos existentes del historial.
 */
export const mapIncidentEventsToHistoryEvents = (
  incidentEvents: IIncidentHistoryEvent[]
): ILogisticServiceHistoryEvent[] => {
  return incidentEvents.map(event => {
    const { name, description } = buildIncidentEventTexts(event);
    return {
      eventType: { name, description },
      createdAt: event.createdAt
    };
  });
};

/**
 * Distribuye los eventos de incidentes en el grupo de historial que
 * corresponda según la fecha de creación del incidente.
 *
 * @param history           Historial del servicio (grupos con eventos)
 * @param incidentEvents    Eventos de incidentes a inyectar
 * @param transitionDates   Mapa opcional: nombre del grupo → fecha del
 *                          cambio de estado (desde `historySL`)
 */
export const injectIncidentEventsIntoHistory = (
  history: ILogisticServiceHistory[],
  incidentEvents: IIncidentHistoryEvent[],
  transitionDates?: Record<string, string>
): ILogisticServiceHistory[] => {
  if (!incidentEvents.length) return history;

  const mappedEvents = mapIncidentEventsToHistoryEvents(incidentEvents);

  /**
   * Extrae los timestamps válidos de un grupo de eventos
   */
  const extractValidTimestamps = (events: ILogisticServiceHistoryEvent[]): number[] => {
    return events
      .map(e => (e.createdAt ? new Date(e.createdAt).getTime() : null))
      .filter((t): t is number => t !== null);
  };

  /**
   * Calcula la frontera entre dos grupos consecutivos
   */
  const calculateBoundary = (
    currentGroup: ILogisticServiceHistory,
    nextGroup: ILogisticServiceHistory,
    currentIndex: number
  ): number | null => {
    if (transitionDates?.[nextGroup.name]) {
      const parsed = new Date(transitionDates[nextGroup.name]).getTime();
      if (!isNaN(parsed)) return parsed;
    }

    const currentTimes = extractValidTimestamps(currentGroup.events);
    if (currentTimes.length > 0) {
      return Math.max(...currentTimes);
    }

    const nextTimes = extractValidTimestamps(nextGroup.events);
    if (nextTimes.length > 0) {
      return Math.min(...nextTimes);
    }

    const firstNonEmptyTime = history
      .slice(currentIndex + 1)
      .map(group => extractValidTimestamps(group.events))
      .find(times => times.length > 0);

    return firstNonEmptyTime ? Math.min(...firstNonEmptyTime) : null;
  };

  const boundaries: number[] = history.slice(0, -1).reduce<number[]>(
    (acc, currentGroup, i) => {
      const nextGroup = history[i + 1];
      const boundary = calculateBoundary(currentGroup, nextGroup, i);
      const prevBoundary = acc[acc.length - 1];
      return [...acc, Math.max(prevBoundary, boundary ?? Infinity)];
    },
    [0]
  );

  const ranges = boundaries.map((start, idx) => ({
    start,
    end: idx < boundaries.length - 1 ? boundaries[idx + 1] : Infinity,
    originalIndex: idx
  }));

  const eventsByOriginalIndex = mappedEvents.reduce<Map<number, ILogisticServiceHistoryEvent[]>>((acc, event, idx) => {
    const incident = incidentEvents[idx];
    const eventTime = incident.createdAt ? new Date(incident.createdAt).getTime() : -1;

    const targetRange = ranges.find(range => eventTime >= range.start && eventTime < range.end);
    const targetIndex = targetRange?.originalIndex ?? ranges[ranges.length - 1].originalIndex;

    const existingEvents = acc.get(targetIndex) ?? [];
    acc.set(targetIndex, [...existingEvents, event]);

    return acc;
  }, new Map());

  return history.map((group, idx) => {
    const incidentEventsForGroup = eventsByOriginalIndex.get(idx);
    if (!incidentEventsForGroup?.length) return group;

    const mergedEvents = [...group.events, ...incidentEventsForGroup].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });

    return { ...group, events: mergedEvents };
  });
};
